// @flow
import React, { useCallback } from 'react';
import type {Node} from 'react';
import {FormattedMessage} from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Box from "@material-ui/core/Box";
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import type {TargetType} from '../../types';
import mapService from '../../services/map';
import TargetListItemContainer from './containers/TargetListItemContainer';

const useStyles = makeStyles(theme => ({
    tableCell: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: theme.spacing(1)
    },
    callsign: {
        width: '50%'
    },
    empty: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.hint,
        textAlign: 'center'
    }
}));

export type Props = $ReadOnly<{
    type: TargetType,
    items: $ReadOnlyArray<string>,
    showSession?: boolean,
    noItemsMessage?: Node
}>;

function TargetList({type, items, showSession, noItemsMessage}: Props) {
    const classes = useStyles();

    const selectTarget = useCallback((target) => {
        mapService.locateTarget(target);
        mapService.selectTarget(target);
    }, []);

    if (!items || items.length === 0) {
        return noItemsMessage || (
            <Box mt={2} p={2} className={classes.empty}>
                <FormattedMessage
                    id="targetList.noItems"
                    defaultMessage="No items"
                />
            </Box>
        );
    }

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell className={classes.callsign} classes={{ root: classes.tableCell }}>
                        <FormattedMessage
                            id="targetList.head.callsign"
                            defaultMessage="Callsign"
                        />
                    </TableCell>
                    <TableCell align="right" classes={{ root: classes.tableCell }}>
                        <FormattedMessage
                            id="targetList.head.lastUpdate"
                            defaultMessage="Last update"
                        />
                    </TableCell>
                    {showSession &&
                    <TableCell colSpan={2} align="right" classes={{ root: classes.tableCell }}>
                        <FormattedMessage
                            id="targetList.head.session"
                            defaultMessage="Session"
                        />
                    </TableCell>
                    }
                    <TableCell classes={{ root: classes.tableCell }} style={{ width: 1 }}>&#8203;{/* zero-width space */}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map((item, index) => (
                    <TargetListItemContainer
                        key={item}
                        className={index === 0 ? classes.callsign : undefined}
                        icao={item}
                        type={type}
                        showSession={showSession}
                        onLocateClick={selectTarget}
                    />
                ))}
            </TableBody>
        </Table>
    );
}

export default TargetList;